// LIB
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';;
import { ContractItem } from '@core/models/contract-item.model';
import { ContractPackChannel } from '@core/models/contract-pack-channel/contract-pack-channel.model';
import { ContractPack } from '@core/models/contract-pack/contract-pack.model';
import { Contract } from '@core/models/contract.model';
import { ThemaChannelGoal } from '@core/models/thema-channel-goal/thema-channel-goal.model';
// MODELS
import { TvChannel } from '@core/models/tv-channel.model';
import { ContractPackService } from '@core/services/contract-pack/contract-pack.service';
import { ThemaChannelGoalService } from '@core/services/thema-channel-goal/thema-channel-goal.service';
// SERVICES
import { TvChannelService } from '@core/services/tv-channel/tv-channel.service';
import { Observable, of, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

interface TvChannelWithRate extends TvChannel {
  rate: number;
}

@Component({
standalone: false,
  selector: 'app-thema-pack-managing-dialog',
  templateUrl: './thema-pack-managing-dialog.component.html',
  styleUrls: ['./thema-pack-managing-dialog.component.scss'],
})
export class ThemaPackManagingDialogComponent implements OnInit, OnDestroy {
  public channels$: Observable<TvChannelWithRate[]>;
  public channelsGoals$: Observable<ThemaChannelGoal[]>;
  public filteredChannelList$: Observable<TvChannelWithRate[]>;
  public isChannelListLoading: boolean = true;
  public componentDestroyed$: Subject<void> = new Subject();
  public selectedChannels: TvChannelWithRate[] = [];
  public searchChannelFormControl = new FormControl(null);
  public packNameFormControl = new FormControl('', [Validators.required]);
  public packDefFormControl = new FormControl(0, [Validators.required]);

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      contract: Contract;
      contractItem: ContractItem;
      modalMode: 'create' | 'edit';
      pack: any;
    },
    public dialogRef: MatDialog,
    private tvChannelService: TvChannelService,
    private themaChannelGoalService: ThemaChannelGoalService,
    private contractPackService: ContractPackService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.isChannelListLoading = true;
    this.getChannels();
    this.getChannelsGoals();
    this.handleSearchChannelValueChange();
    this.setDefaultDefValue();
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.unsubscribe();
  }

  public closeDialog(sendSuccess = null): void {
    this.dialogRef.openDialogs.find(dialog => dialog.componentInstance instanceof ThemaPackManagingDialogComponent).close(sendSuccess);
  }

  public loadExistingPackData(): void {
    this.packNameFormControl.setValue(this.data.pack.label);

    this.data.pack.contractPackChannels.map((packChannel: ContractPackChannel) => {
      this.filteredChannelList$.subscribe(channels => {
        const channelIndex = channels.findIndex(channel => channel.id === packChannel.channel.id);
        channels[channelIndex].rate = packChannel.rate;
      });

      this.selectedChannels.push({
        ...packChannel.channel,
        rate: packChannel.rate,
      });
    });
  }

  public getChannels(): void {
    this.tvChannelService.getThemaChannels().subscribe(
      (channels: TvChannel[]) => {
        this.channels$ = of(
          channels.map(channel => {
            return {
              ...channel,
              rate: 0,
            };
          })
        );
        this.filteredChannelList$ = this.channels$;

        if (this.data.modalMode === 'edit' && this.data.pack) {
          this.loadExistingPackData();
        }
      },
      error => {
        this.isChannelListLoading = false;
        console.error('Error loading channels', error);
      }
    );
  }

  public getChannelsGoals(): void {
    this.themaChannelGoalService.getList(Number(this.data.contractItem.year)).subscribe((goals: ThemaChannelGoal[]) => {
      this.isChannelListLoading = false;
      this.channelsGoals$ = of(goals);
    });
  }

  public handleSearchChannelValueChange() {
    this.searchChannelFormControl.valueChanges.pipe(takeUntil(this.componentDestroyed$)).subscribe(value => {
      this.searchChannelFormControl.patchValue(value.toUpperCase(), { emitEvent: false });
      this.filteredChannelList$ = this.channels$.pipe(
        map(channels => channels.filter(channel => channel.name.toLowerCase().includes(value.toLowerCase())))
      );
    });
  }

  public setDefaultDefValue() {
    if (this.data.modalMode === 'edit' && this.data.pack) {
      this.packDefFormControl.setValue(this.data.pack.defTx);
    } else {
      this.packDefFormControl.setValue(-40);
    }
  }

  public updateChannelsRate() {
    // get the sum of all the selected channels goals
    const totalSelectedGoals = this.channelsGoals$.pipe(
      map(channels =>
        channels.reduce((sum, channelGoal) => {
          if (this.selectedChannels.some(selectedChannel => selectedChannel.channelCode === channelGoal.channelCode)) {
            return sum + channelGoal.goal;
          }

          return sum;
        }, 0)
      )
    );

    // recalculate the rate of the selected channels
    this.channelsGoals$.subscribe(channelsGoals => {
      totalSelectedGoals.subscribe(total => {
        if (total > 0) {
          this.selectedChannels = this.selectedChannels.map(channel => {
            const channelGoal = channelsGoals.find(goal => goal.channelCode === channel.channelCode);

            if (channelGoal) {
              // get the rate of the channel at 1 decimal
              channel.rate = Math.round((channelGoal.goal / total) * 10000) / 100;
              this.updateSelectedChannelRate(channel, channel.rate);
            }

            return channel;
          });
        }

        if (this.getTotalRate() !== 100) {
          // find the channel with the highest rate and set the rate to match 100
          const maxRateChannel = this.selectedChannels?.reduce((prev, current) => (prev.rate > current.rate ? prev : current));

          // find if total is greater or lower than 100
          const totalRate = this.getTotalRate();
          const diff = Math.round((totalRate > 100 ? totalRate - 100 : 100 - totalRate) * 10) / 10;

          maxRateChannel.rate = totalRate > 100 ? maxRateChannel.rate - diff : maxRateChannel.rate + diff;

          this.updateSelectedChannelRate(maxRateChannel, maxRateChannel.rate);
        }
      });
    });
  }

  // This function is here to force the relation with matChipList in Edit mode.
  public updateSelectedChannelRate(channel: TvChannelWithRate, newRate: number): void {
    const index = this.selectedChannels.findIndex(item => item.id === channel.id);
    if (index !== -1) {
      this.selectedChannels[index] = { ...channel, rate: newRate };
      this.selectedChannels = [...this.selectedChannels];
    }
  }

  public isChannelSelected(channel: TvChannelWithRate): boolean {
    return this.selectedChannels.some(item => item.id === channel.id);
  }

  public onChannelSelectChange(event: any, channel: TvChannelWithRate, index: number) {
    if (event.checked) {
      this.selectedChannels.push(channel);
      this.updateChannelsRate();
    } else {
      this.selectedChannels = this.selectedChannels.filter(item => item.id !== channel.id);
      this.updateChannelsRate();
    }
  }

  public removeChannelItem(itemToRemove: TvChannelWithRate) {
    this.selectedChannels = this.selectedChannels.filter(item => item.id !== itemToRemove.id);
    this.updateChannelsRate();
  }

  public displaySnackBar(message: string, panelClass: string) {
    this.snackBar.open(message, null, {
      duration: 4000,
      verticalPosition: 'top',
      panelClass: [panelClass],
    });
  }

  public getTotalRate(): number {
    const rawSum = this.selectedChannels.reduce((sum, channel) => sum + channel.rate, 0);

    return Math.round(rawSum * 10) / 10;
  }

  public isValidationButtonDisabled(): boolean {
    const totalRate = this.getTotalRate();
    return !this.packNameFormControl.valid || this.selectedChannels.length === 0 || totalRate !== 100;
  }

  public savePack(): void {
    const packName = this.packNameFormControl.value;
    const packDef = this.packDefFormControl.value;
    const packChannels = this.selectedChannels.map(channel => {
      return {
        channel: channel.id,
        rate: channel.rate,
      };
    });
    const data = {
      label: packName,
      defTx: packDef,
      year: this.data.contractItem.year,
      isActive: 1,
      contractPackChannels: packChannels,
    };

    if (this.data.modalMode === 'create') {
      this.contractPackService.createPack(data).subscribe(
        () => {
          this.displaySnackBar('Pack créé avec succès', 'tv-chip-success');
          this.closeDialog(true);
        },
        error => {
          this.displaySnackBar('Erreur lors de la création du pack', 'tv-chip-error');
          console.error('Error saving pack', error);
        }
      );
    } else {
      this.contractPackService.updatePack(this.getPackPayloadData(data)).subscribe(
        () => {
          this.displaySnackBar('Pack modifié avec succès', 'tv-chip-success');
          this.closeDialog(true);
        },
        error => {
          this.displaySnackBar('Erreur lors de la modification du pack', 'tv-chip-error');
          console.error('Error updating pack', error);
        }
      );
    }
  }

  public getPackPayloadData(data: any): ContractPack {
    return {
      id: this.data.pack.id,
      label: data.label,
      year: data.year,
      defTx: data.defTx,
      isActive: data.isActive,
      contractPackChannels: data.contractPackChannels,
    };
  }
}
